import type { CatalogItem } from '../../../redux/api/fymAPI.types';

/** What the person tried to add when the catalog answered "already in your catalog" (409). */
export interface AddedSongFacts {
  title: string;
  catalogTrackMaid?: string;
  /** The service links of a pasted link (the link itself and what the resolver found). */
  serviceLinks?: Record<string, string>;
}

function titleKey(title: string): string {
  return title.trim().toLowerCase();
}

/** A link with no query, no fragment and no end slash, in lower case: one song, one key. */
function linkKey(url: string): string {
  const [withoutQuery = ''] = url.trim().split(/[?#]/);
  return withoutQuery.replace(/\/+$/, '').toLowerCase();
}

/**
 * The catalog row that made the add fail with 409 (design c3 "Already in the catalog"). The API
 * refuses a release with the same catalog track, the same title, or a shared service link
 * (`assertReleaseIsNotInCatalog`). The page looks for the row in the same order, so "Open its
 * release page" also shows for a pasted link whose title differs from the row.
 */
export function findExistingSong(items: readonly CatalogItem[], added: AddedSongFacts): CatalogItem | undefined {
  const { catalogTrackMaid } = added;
  if (catalogTrackMaid !== undefined) {
    const byTrack = items.find((item) => item.catalogTrackMaid === catalogTrackMaid);
    if (byTrack !== undefined) {
      return byTrack;
    }
  }
  const wantedLinks = new Set(Object.values(added.serviceLinks ?? {}).map(linkKey));
  if (wantedLinks.size > 0) {
    const byLink = items.find((item) =>
      Object.values(item.serviceLinks ?? {}).some((url) => typeof url === 'string' && wantedLinks.has(linkKey(url)))
    );
    if (byLink !== undefined) {
      return byLink;
    }
  }
  const wantedTitle = titleKey(added.title);
  return items.find((item) => titleKey(item.title) === wantedTitle);
}
